import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getAccentColor } from '../lib/jlfcTheme';
import { NavLink, Box, Text, Loader, Center, useMantineTheme } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import SearchFilterInput from './SearchFilterInput';
import * as TablerIcons from '@tabler/icons-react';
import { authFetch } from '../lib/api';
import { useActiveExploitant } from '../context/ActiveExploitantContext';

/** Indentation des sous-liens (prop Mantine childrenOffset, voir doc NavLink) */
const CHILDREN_OFFSET = 'md';

export default function DynamicMenu({
  onSelectModule,
  currentPath,
  openMenus,
  setOpenMenus,
  openSubMenus,
  setOpenSubMenus,
}) {
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { isRoot, activeId } = useActiveExploitant();
  const menuScope = isRoot ? activeId : 'tenant';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await authFetch('/api/menu_items');
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data.success) {
          setLoadError(
            data.message ||
              (res.status === 404
                ? 'API menu introuvable — redémarrez le serveur (npm run server).'
                : `Erreur menu (${res.status})`)
          );
          return;
        }
        setMenuData(data.data);
        setLoadError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('[DynamicMenu]', err);
        setLoadError('Impossible de charger le menu. Vérifiez que l’API tourne sur le port 5060.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [menuScope]);

  /** Ouvre l’accordéon parent quand une sous-route est active */
  useEffect(() => {
    if (!menuData.length || !currentPath) return;

    const openAncestors = (items, level = 0) => {
      for (const item of items) {
        if (!item.children?.length) continue;
        if (!pathMatches(item, currentPath)) continue;

        if (level === 0) {
          setOpenMenus((prev) => ({ ...prev, [`menu_${item.id}`]: true }));
        } else {
          setOpenSubMenus((prev) => ({ ...prev, [`submenu_${item.id}`]: true }));
        }
        openAncestors(item.children, level + 1);
      }
    };

    openAncestors(menuData);
  }, [currentPath, menuData, setOpenMenus, setOpenSubMenus]);

  const theme = useMantineTheme();
  const palette = theme.colors[theme.primaryColor] || theme.colors.teal;

  const getIcon = (iconName, isActive = false) => {
    if (!iconName) return null;
    const IconComponent = TablerIcons[iconName];
    if (!IconComponent) return null;
    const color = isActive ? palette[7] : palette[6];
    return <IconComponent size={16} color={color} stroke={1.5} />;
  };

  const setMenuOpened = (itemId, level, opened) => {
    if (level === 0) {
      setOpenMenus(() => {
        const next = {};
        if (opened) next[`menu_${itemId}`] = true;
        return next;
      });
      if (opened) setOpenSubMenus({});
    } else {
      setOpenSubMenus(() => {
        const next = {};
        if (opened) next[`submenu_${itemId}`] = true;
        return next;
      });
    }
  };

  const filterMenuItems = useCallback((items, query) => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    const out = [];
    for (const item of items) {
      const matches =
        item.label.toLowerCase().includes(q) ||
        (item.route && item.route.toLowerCase().includes(q));
      const children = item.children ? filterMenuItems(item.children, query) : [];
      if (matches || children.length > 0) {
        out.push({ ...item, children });
      }
    }
    return out;
  }, []);

  const filteredMenuData = useMemo(
    () => filterMenuItems(menuData, searchQuery),
    [menuData, searchQuery, filterMenuItems]
  );

  /**
   * NavLink imbriqués (recommandation Mantine) — évite Collapse + Box pl qui décale les fonds actifs.
   * @see https://mantine.dev/core/nav-link/#nested-navlinks
   */
  const renderMenuItem = (item, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isOpen =
      level === 0 ? openMenus[`menu_${item.id}`] : openSubMenus[`submenu_${item.id}`];
    const isActive = Boolean(item.route && currentPath === item.route);

    if (hasChildren) {
      return (
        <NavLink
          key={item.id}
          label={item.label}
          leftSection={getIcon(item.icon, isActive)}
          opened={Boolean(isOpen)}
          onChange={(opened) => setMenuOpened(item.id, level, opened)}
          childrenOffset={CHILDREN_OFFSET}
          active={isActive}
          variant={isActive ? 'light' : 'subtle'}
          color={getAccentColor()}
        >
          {item.children.map((child) => renderMenuItem(child, level + 1))}
        </NavLink>
      );
    }

    return (
      <NavLink
        key={item.id}
        label={item.label}
        leftSection={getIcon(item.icon, isActive)}
        onClick={() => item.route && onSelectModule(item.route)}
        active={isActive}
        variant="light"
        color={getAccentColor()}
      />
    );
  };

  if (loading) {
    return (
      <Center py="md">
        <Loader size="sm" color={getAccentColor()} />
      </Center>
    );
  }

  if (loadError) {
    return (
      <Text size="xs" c="red" px="xs">
        {loadError}
      </Text>
    );
  }

  return (
    <>
      <Box mb="md">
        <SearchFilterInput
          placeholder="Rechercher..."
          name="jlfc-menu-filter"
          aria-label="Filtrer le menu"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          size="sm"
        />
      </Box>
      {filteredMenuData.length === 0 && searchQuery.trim() ? (
        <Text size="sm" c="dimmed" fs="italic" px="xs">
          Aucun résultat
        </Text>
      ) : (
        filteredMenuData.map((item) => renderMenuItem(item))
      )}
    </>
  );
}

function pathMatches(item, path) {
  if (item.route === path) return true;
  return (item.children || []).some((c) => pathMatches(c, path));
}
